import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export interface MasterOverviewResponse {
  mrrCents: number;
  workspacesTotal: number;
  workspacesActive: number;
  workspacesOnboarding: number;
  workspacesAttention: Array<{
    id: string;
    name: string;
    slug: string;
    status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  }>;
  billing: { total: number; active: number; pending: number; cancelled: number };
  recentAudit: Array<{
    id: string;
    action: string;
    target: string | null;
    workspaceId: string | null;
    createdAt: string;
    metadata: Record<string, unknown> | null;
  }>;
}

export function useMasterOverview() {
  return useQuery({
    queryKey: ['master', 'overview'],
    queryFn: () => api.get<MasterOverviewResponse>('/v1/master/overview'),
    staleTime: 30_000,
  });
}

export interface MasterWorkspaceListItem {
  id: string;
  slug: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  createdAt: string;
  adminEmail: string | null;
  userCount: number;
}

export function useMasterWorkspaces() {
  return useQuery({
    queryKey: ['master', 'workspaces'],
    queryFn: () => api.get<{ items: MasterWorkspaceListItem[] }>('/v1/master/workspaces'),
    staleTime: 30_000,
  });
}

export interface MasterBillingStats {
  total: number;
  active: number;
  pending: number;
  cancelled: number;
  mrr: number;
}

export interface MasterBillingSubscription {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  plan: string;
  cycle: string;
  value: number;
  status: string;
  paymentMethod: string | null;
  nextDueDate: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export function useMasterBilling() {
  return useQuery({
    queryKey: ['master', 'billing'],
    queryFn: async () => {
      const [stats, subs] = await Promise.all([
        api.get<MasterBillingStats>('/v1/master/billing/stats'),
        api.get<{ items: MasterBillingSubscription[] }>('/v1/master/billing/subscriptions'),
      ]);
      return { stats, subscriptions: subs.items };
    },
    staleTime: 30_000,
  });
}

export interface MasterAuditRow {
  id: string;
  workspaceId: string | null;
  platformUserId: string | null;
  workspaceUserId: string | null;
  action: string;
  target: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export function useMasterAudit() {
  return useQuery({
    queryKey: ['master', 'audit'],
    queryFn: () => api.get<{ items: MasterAuditRow[] }>('/v1/master/audit'),
    staleTime: 30_000,
  });
}

/**
 * Inicia impersonação de um workspace. O backend troca o cookie de sessão.
 * Após sucesso, força refresh do `/auth/me` (atualiza `actingAs`) e invalida queries.
 */
export function useImpersonateWorkspace() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  return useMutation({
    mutationFn: (workspaceId: string) =>
      api.post(`/v1/master/workspaces/${workspaceId}/impersonate`, {}),
    onSuccess: async () => {
      await refresh();
      await qc.invalidateQueries({ queryKey: ['master'] });
    },
  });
}

/** Encerra impersonação — backend re-emite cookie do platform user. */
export function useExitImpersonation() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  return useMutation({
    mutationFn: () => api.post('/v1/master/impersonate/exit', {}),
    onSuccess: async () => {
      await refresh();
      await qc.invalidateQueries({ queryKey: ['master'] });
    },
  });
}
